import { z } from 'zod'
import type { Application } from '#/domain/application/Application'
import { ApplicationDtoSchema } from './ApplicationDto'

export interface InvalidApplication {
  index: number
  applicationId?: string
  issues: string
}

const INVALID_RESPONSE_MESSAGE = 'Invalid applications response'

const readApplicationId = (raw: unknown): string | undefined => {
  if (typeof raw !== 'object' || raw === null || !('Id' in raw)) return undefined
  return typeof raw.Id === 'string' ? raw.Id : undefined
}

/**
 * Validates the `GET v1/application` payload and returns the usable applications.
 *
 * An invalid application is skipped and reported through `onInvalid`, so one bad entry cannot hide the
 * rest. If the list is not empty and every entry is invalid, the contract is broken, so this throws
 * instead of returning an empty list that looks like "no applications".
 *
 * @throws Error when the payload is not an array or no application is valid
 */
export function parseApplications(
  raw: unknown,
  onInvalid?: (invalid: InvalidApplication) => void,
): Application[] {
  if (!Array.isArray(raw)) {
    throw new Error(INVALID_RESPONSE_MESSAGE)
  }

  const applications: Application[] = []

  raw.forEach((entry, index) => {
    const parsed = ApplicationDtoSchema.safeParse(entry)
    if (parsed.success) {
      applications.push(parsed.data)
      return
    }
    onInvalid?.({
      index,
      applicationId: readApplicationId(entry),
      issues: z.prettifyError(parsed.error),
    })
  })

  if (raw.length > 0 && applications.length === 0) {
    throw new Error(INVALID_RESPONSE_MESSAGE)
  }

  return applications
}
